/** @jsx isolateComponent */

import { Fragment } from 'react';

import { Price } from '@zapier/currency';
import { Button, Link } from '@zapier/design-system';
import { isolateComponent } from '@zapier/style-encapsulation';

import { UpgradeButtonToPricingPage } from '@zapier/upsells/src/components/UpgradeModal/UpgradeModalLayout/UpgradeNudge/UpgradeButtonToPricingPage';
import { Plan, Currency } from '@zapier/upsells/src/types';

const TaskHistoryButton = () => (
  <Button color="secondary" href="/app/history/usage" key="see-task-history">
    See task history
  </Button>
);

/**
 * Note: Upgrade buttons in these views take the user to the upgrade
 * page rather than doing the upgrade inline because these nudges could
 * be used in `web-ssr`, and the payment app doesn't work in `web-ssr`.
 */
const views = {
  ['DEFAULT']: ({
    currency,
    currentPlan,
    destinationPlan,
  }: {
    currency: Currency;
    currentPlan: Plan;
    destinationPlan: Plan;
  }) => ({
    bodyText: (
      <Fragment>
        Keep your Zaps running by upgrading your plan with more{' '}
        <Link href="/help/manage/history/learn-about-tasks-in-zapier">
          tasks
        </Link>
        .{' '}
        <strong>
          Get access to {Number(destinationPlan.tasks).toLocaleString()} tasks
          with the {destinationPlan.type} plan for{' '}
          <Price currency={currency}>
            {destinationPlan.interval === 'year'
              ? destinationPlan.amount / 12
              : destinationPlan.amount}
          </Price>{' '}
          per month.
        </strong>
      </Fragment>
    ),

    buttons: [
      <TaskHistoryButton />,
      <UpgradeButtonToPricingPage
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        destinationPlanName={destinationPlan.name}
      />,
    ],

    type: 'DEFAULT',
  }),

  ['DEFAULT_AUTO_UPGRADE']: ({
    autoUpgradeToPlan,
  }: {
    autoUpgradeToPlan: Plan;
  }) => ({
    bodyText: (
      <Fragment>
        Because you've opted into the{' '}
        <Link href="/l/automatic-task-upgrades">automatic task upgrade</Link>{' '}
        feature, if you reach 100% of your task limit, you will be upgraded to
        the {autoUpgradeToPlan.type} plan with{' '}
        {Number(autoUpgradeToPlan.tasks).toLocaleString()} tasks.{' '}
        <strong>No action from you is required.</strong>
      </Fragment>
    ),

    buttons: [
      <TaskHistoryButton />,
      <Button
        color="primary"
        href="/app/settings/billing"
        key="manage-auto-upgrade"
      >
        Manage setting
      </Button>,
    ],

    type: 'DEFAULT_AUTO_UPGRADE',
  }),
};

type Props = {
  autoUpgradeToPlan: Plan | null | undefined;
  currency: Currency;
  currentPlan: Plan;
  destinationPlan: Plan;
  isAutoUpgradeEnabled: boolean;
};

export const getActiveView = ({
  autoUpgradeToPlan,
  currency,
  currentPlan,
  destinationPlan,
  isAutoUpgradeEnabled,
}: Props) => {
  if (isAutoUpgradeEnabled && autoUpgradeToPlan) {
    return views.DEFAULT_AUTO_UPGRADE({
      autoUpgradeToPlan,
    });
  }

  return views.DEFAULT({
    currency,
    currentPlan,
    destinationPlan,
  });
};
